import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';

import {
  CloseCircleOutline,
  CheckOutline,
  DropdownInput,
  PayCalendarSalaryAllowance,
  useToast,
} from '@aslanpay/portal-common-lib';

import {
  getOdpAllowancePercentagePercentageOptions,
  getGlobalAllowancePercentageOptions,
} from 'app/components/PayCalendars/options';

import useFetchIndividual from 'app/hooks/useFetchIndividual';
import useUpdateIndividual from 'app/hooks/useUpdateIndividual';

import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';

import editSalaryAllowanceFormSchema from 'app/utils/schemas/editSalaryAllowance';

import { EditSalaryAllowanceModalProps } from './types';

interface EditSalaryAllowanceFormFields {
  odpAllowancePercentage?: number;
  customOdpAllowancePercentage?: number;
  globalAllowancePercentage?: number;
  customGlobalAllowancePercentage?: number;
}

function EditSalaryAllowanceModal({ id, isOpen, onClose, onEdit }: EditSalaryAllowanceModalProps) {
  const toast = useToast();

  const { data: employee, isLoading: isLoadingEmployee } = useFetchIndividual(id, isOpen);

  const { data: payCalendar, isLoading: isLoadingPayCalendar } = useFetchPayCalendar(
    employee?.payCalendarId
  );

  const { data: companyConfiguration, isLoading: isLoadingCompanyConfiguration } =
    useFetchCompanyConfiguration(employee?.companyId);

  const isOnDemandPayEnabled =
    companyConfiguration?.modulesConfig?.modules?.ON_DEMAND_PAY === 'ENABLED';
  const isReserveEnabled = companyConfiguration?.modulesConfig?.modules?.RESERVE === 'ENABLED';

  // Create validation schema
  const validationSchema = editSalaryAllowanceFormSchema(
    isOnDemandPayEnabled,
    isReserveEnabled,
    employee?.globalAllowancePercentage,
    payCalendar?.globalAllowancePercentage
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<EditSalaryAllowanceFormFields>({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
  });

  // Watchers for conditional rendering
  const watchOdpAllowancePercentage = watch('odpAllowancePercentage');
  const watchGlobalAllowancePercentage = watch('globalAllowancePercentage');

  const handleClose = () => {
    reset();
    onClose();
  };

  // Initialize form with current values when modal opens
  useEffect(() => {
    if (isOpen && employee) {
      const formData: EditSalaryAllowanceFormFields = {};

      // Initialize ODP allowance
      const currentOdpValue =
        employee.odpAllowancePercentage ?? payCalendar?.odpAllowancePercentage;

      if (currentOdpValue !== undefined) {
        // Check if it's a custom value (not in the predefined options)
        const odpOptions = getOdpAllowancePercentagePercentageOptions();

        const isCustomOdp = !odpOptions.some((option) => {
          return option.value === currentOdpValue;
        });

        if (isCustomOdp) {
          formData.odpAllowancePercentage = 200; // Custom option
          formData.customOdpAllowancePercentage = currentOdpValue;
        } else {
          formData.odpAllowancePercentage = currentOdpValue;
        }
      }

      // Initialize Global allowance
      const currentGlobalValue =
        employee.globalAllowancePercentage ?? payCalendar?.globalAllowancePercentage;

      if (currentGlobalValue !== undefined) {
        // Check if it's a custom value (not in the predefined options)
        const globalOptions = getGlobalAllowancePercentageOptions();

        const isCustomGlobal = !globalOptions.some((option) => {
          return option.value === currentGlobalValue;
        });

        if (isCustomGlobal) {
          formData.globalAllowancePercentage = 200; // Custom option
          formData.customGlobalAllowancePercentage = currentGlobalValue;
        } else {
          formData.globalAllowancePercentage = currentGlobalValue;
        }
      }

      reset(formData);
    }
  }, [isOpen, employee, payCalendar, reset]);

  const { mutate: updateSalaryAllowance, isLoading: isUpdatingSalaryAllowance } =
    useUpdateIndividual({
      onSuccess: () => {
        toast({
          position: 'top-right',
          status: 'success',
          title: 'Success',
          description: 'The allowance limits for the employee were updated!',
        });

        onEdit();
        handleClose();
      },
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description:
            'Sorry, it was not possible to edit the allowance limits for the employee. Please try again.',
        });
      },
    });

  const onSubmit = (data: EditSalaryAllowanceFormFields) => {
    if (employee) {
      const updateData = { ...employee };

      // Calculate current ODP allowance value
      const currentOdpValue =
        employee.odpAllowancePercentage ?? payCalendar?.odpAllowancePercentage;
      const newOdpValue =
        data.odpAllowancePercentage === 200
          ? data.customOdpAllowancePercentage
          : data.odpAllowancePercentage;

      // Only include ODP allowance if it was actually changed
      if (data.odpAllowancePercentage !== undefined && newOdpValue !== currentOdpValue) {
        updateData.odpAllowancePercentage = newOdpValue as PayCalendarSalaryAllowance;
      }
      // Calculate current Global allowance value
      const currentGlobalValue =
        employee.globalAllowancePercentage ?? payCalendar?.globalAllowancePercentage;
      const newGlobalValue =
        data.globalAllowancePercentage === 200
          ? data.customGlobalAllowancePercentage
          : data.globalAllowancePercentage;

      // Only include Global allowance if it was actually changed
      if (data.globalAllowancePercentage !== undefined && newGlobalValue !== currentGlobalValue) {
        // console.log('Global Allowance changed:', {
        //   current: currentGlobalValue,
        //   new: newGlobalValue,
        // });
        updateData.globalAllowancePercentage = newGlobalValue as PayCalendarSalaryAllowance;
      } else {
        // console.log('Global Allowance unchanged:', {
        //   current: currentGlobalValue,
        //   new: newGlobalValue,
        // });
      }

      // console.log('API Payload:', updateData);
      updateSalaryAllowance(updateData);
    }
  };

  const odpAllowancePercentageOptions = getOdpAllowancePercentagePercentageOptions();
  const globalAllowancePercentageOptions = getGlobalAllowancePercentageOptions();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Edit employee allowance limits</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Edit employee allowance limits"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        {isLoadingEmployee || isLoadingPayCalendar || isLoadingCompanyConfiguration ? (
          <Center width="full" height="full" flexGrow={1}>
            <Spinner color="brand.purple" size="xl" mb="50px" />
          </Center>
        ) : (
          <ModalBody mt="20px">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing="30px">
                {/* Global Allowance Section */}
                {isReserveEnabled && (
                  <FormControl isInvalid={!!errors?.globalAllowancePercentage?.message} isRequired>
                    <FormLabel>Global allowance percentage</FormLabel>
                    <Controller
                      control={control}
                      name="globalAllowancePercentage"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <DropdownInput
                          placeholder="Select a percentage"
                          defaultValue={globalAllowancePercentageOptions.find(
                            (option) => option.value === value
                          )}
                          options={globalAllowancePercentageOptions}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      )}
                    />
                    <FormErrorMessage>
                      {errors?.globalAllowancePercentage?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}

                {/* Custom Global Allowance Input */}
                {isReserveEnabled && watchGlobalAllowancePercentage === 200 && (
                  <FormControl
                    isInvalid={!!errors?.customGlobalAllowancePercentage?.message}
                    isRequired
                  >
                    <FormLabel>What custom percentage would you like?</FormLabel>
                    <Controller
                      control={control}
                      name="customGlobalAllowancePercentage"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <InputGroup>
                          <Input
                            type="number"
                            placeholder="Enter percentage from 0 to 100"
                            value={value || ''}
                            onChange={onChange}
                            onBlur={onBlur}
                          />
                          <InputRightElement>
                            <Text color="brand.darkGray">%</Text>
                          </InputRightElement>
                        </InputGroup>
                      )}
                    />
                    <FormErrorMessage>
                      {errors?.customGlobalAllowancePercentage?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}

                {/* On Demand Pay Allowance Section */}
                {isOnDemandPayEnabled && (
                  <FormControl isInvalid={!!errors?.odpAllowancePercentage?.message} isRequired>
                    <FormLabel>ODP allowance percentage</FormLabel>
                    <Controller
                      control={control}
                      name="odpAllowancePercentage"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <DropdownInput
                          placeholder="Select a percentage"
                          defaultValue={odpAllowancePercentageOptions.find(
                            (option) => option.value === value
                          )}
                          options={odpAllowancePercentageOptions}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      )}
                    />
                    <FormErrorMessage>{errors?.odpAllowancePercentage?.message}</FormErrorMessage>
                  </FormControl>
                )}

                {/* Custom ODP Allowance Input */}
                {isOnDemandPayEnabled && watchOdpAllowancePercentage === 200 && (
                  <FormControl
                    isInvalid={!!errors?.customOdpAllowancePercentage?.message}
                    isRequired
                  >
                    <FormLabel>What custom percentage would you like?</FormLabel>
                    <Controller
                      control={control}
                      name="customOdpAllowancePercentage"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <InputGroup>
                          <Input
                            type="number"
                            placeholder="Enter percentage from 0 to 100"
                            value={value || ''}
                            onChange={onChange}
                            onBlur={onBlur}
                          />
                          <InputRightElement>
                            <Text color="brand.darkGray">%</Text>
                          </InputRightElement>
                        </InputGroup>
                      )}
                    />
                    <FormErrorMessage>
                      {errors?.customOdpAllowancePercentage?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Stack>
            </form>
          </ModalBody>
        )}
        <ModalFooter>
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            rightIcon={<CheckOutline boxSize="20px" />}
            isLoading={isUpdatingSalaryAllowance}
            isDisabled={!isValid}
            onClick={handleSubmit(onSubmit)}
          >
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default EditSalaryAllowanceModal;
